// The texts of «Guida a Politor» (/guida): one section per feature of the
// app as it is today, each with its points, its legend where the icons need
// one, and the links to the screens it describes. The labels the app exports
// are imported, so they cannot drift; the others are quoted as the screens
// show them and tests/guide.test.tsx checks them against the screens.
import type { IconName } from "../../components/Icon";
import { IN_DEVELOPMENT } from "../../components/InDevelopment";
import { OPEN_PAGE } from "../../components/news/NewsCard";
import { BACK_TO_NEWS, CONTINUE_READING } from "../../components/news/NewsReader";
import { OPEN_OFFICIAL } from "../../components/parliament/OfficialPage";
import { INDEX_TITLE } from "../../components/parliament/SectionIndex";
import { RESERVED_NAME } from "../feeds/adapt";
import { SITTINGS_VIEW_PATH } from "../parliament/adapt";

/** A link at the end of a section: the screen it describes. */
export interface GuideLink {
  to: string;
  label: string;
}

/** A row of the legend: one of the app's icons (or the real ingestion icon) and what it means. */
export interface LegendItem {
  key: string;
  icon: IconName | "ingestion";
  /** Extra classes of the icon (the chevron to the right is the one down, rotated). */
  className?: string;
  text: string;
}

export interface GuideSection {
  /** The anchor of the section (#id) and its entry in the index. */
  id: string;
  title: string;
  points: readonly string[];
  legend?: readonly LegendItem[];
  links: readonly GuideLink[];
}

export const GUIDE_TITLE = "Guida a Politor";
export const GUIDE_INTRO =
  "Politor raccoglie le news delle fonti istituzionali e i documenti del Parlamento. Questa guida descrive le " +
  "schermate di oggi, una per sezione: ogni sezione finisce con il collegamento alla schermata che descrive.";
export const LEGEND_LABEL = "Legenda";

/** A label as the texts quote it: «…». */
const q = (label: string) => `«${label}»`;

const LATEST: GuideLink = { to: "/news", label: "Vai a Ultime news" };
const SAVED: GuideLink = { to: "/news?tab=saved", label: "Vai a News salvate" };
const FEED: GuideLink = { to: "/news?tab=feed", label: "Vai a Il mio feed" };
const SITTINGS: GuideLink = { to: "/parlamento", label: "Vai alle sedute del Parlamento" };

export const GUIDE_SECTIONS: readonly GuideSection[] = [
  {
    id: "ultime-news",
    title: "Ultime news",
    points: [
      "«Ultime news» ha due liste: «News dall’ultimo accesso», con a destra la data e l’ora della tua visita precedente, " +
        "e «News precedenti». La visita si registra ogni volta che apri l’area News.",
      "Le news sono in ordine di data di pubblicazione, le più recenti in alto; nello stesso giorno viene prima " +
        "l’ultima arrivata o aggiornata. «News dall’ultimo accesso» raccoglie quello che è arrivato dopo la tua " +
        "visita ed è stato pubblicato al massimo una settimana prima: una news più vecchia scaricata solo ora, come " +
        "quelle dell’archivio di una fonte, è in «News precedenti», al suo giorno.",
      "Il pannello «Gestione delle fonti» a sinistra: ogni casella aggiunge o toglie una fonte dalle liste; «Seleziona " +
        "tutte», «Deseleziona tutte», «Seleziona i preferiti» e «Seleziona gli altri» agiscono su tutte insieme. La " +
        "scelta resta nell’indirizzo della pagina, che puoi condividere. Il pannello è chiuso quando apri Politor: " +
        "la striscia con l’icona lo apre, il chevron accanto al titolo lo chiude, e resta come lo lasci in ogni pagina. " +
        "Aperto, resta in vista mentre scorri le news.",
      "L’albero parte da Parlamento, con Camera e Senato e le loro sezioni, poi gli altri enti in ordine alfabetico; " +
        "«Cerca la fonte» filtra l’albero per nome.",
      "Il nome di un ente o di una sezione, o un chip su una card, mostra solo quella fonte: il titolo della lista dice " +
        "quale e, sotto il titolo, «Rimuovi il filtro» torna a tutte le fonti.",
      `Il pin accanto al nome dell’ente lo mette tra i preferiti e, nello stesso riquadro, nelle tue cartelle: ${q(RESERVED_NAME)} ` +
        "è sempre spuntata, «Nuova cartella» ne crea una. Con il pin acceso il riquadro gestisce le cartelle e offre " +
        "«Rimuovi dai preferiti».",
      "Sotto l’albero, «Fonti eliminate» elenca le fonti tolte dal catalogo.",
      "Il logo in alto a sinistra riporta a «Ultime news», senza filtri, da qualsiasi pagina.",
    ],
    links: [LATEST],
  },
  {
    id: "card",
    title: "La card e la pagina di una news",
    points: [
      "Ogni card porta due chip con l’imbuto, l’ente e la sezione: cliccandoli filtri le news su quella fonte. Accanto " +
        "alla data c’è il logo dell’ente, o le sue iniziali quando il logo manca.",
      "La riga della data dice quando la fonte ha pubblicato la news e quando Politor l’ha scaricata: «12 ottobre 2026 " +
        "(scaricato alle 09:32)».",
      `Il chevron in basso a destra: verso il basso («Espandi news») apre la news sul posto, quando il testo è breve e ` +
        `senza allegati; verso destra (${q(OPEN_PAGE)}) la apre nella sua pagina.`,
      "Nella card aperta trovi «Apri link esterno», che apre il sito della fonte in una nuova scheda, e «Rielabora con " +
        "AI», ancora in sviluppo.",
      "La pagina della news ha il titolo, la riga dei dettagli, gli «Allegati» con «Apri» per ciascuno, l’«Iter» di un " +
        "atto con la tappa in corso in evidenza e, per un documento del Parlamento, la sezione «Parlamento» e la «Pagina " +
        "ufficiale».",
      `Un testo lungo arriva a pezzi: ${q(CONTINUE_READING)} ne mostra un altro e il contatore dice quanto hai letto. ` +
        `${q(BACK_TO_NEWS)} riporta alla lista con gli stessi filtri.`,
    ],
    links: [LATEST],
  },
  {
    id: "salvate",
    title: "News salvate",
    points: [
      "Il segnalibro su una card o nella pagina («Salva news») porta la news in «News salvate»; premerlo di nuovo " +
        "(«Rimuovi dalle news salvate») la toglie.",
      "Le news salvate sono raggruppate per mese di pubblicazione; «Filtro per data» elenca gli anni e i mesi che ne hanno.",
      "Un mese si apre sulle sue settimane («5–11 ottobre»): puoi spuntare mesi interi o singole settimane; se togli " +
        "l’ultima spunta tornano spuntati tutti.",
      "«Pulisci tutto» elimina tutte le news salvate dopo una conferma: non si può annullare.",
    ],
    links: [SAVED],
  },
  {
    id: "feed",
    title: "Il mio feed",
    points: [
      `«Il mio feed» mostra le news delle tue cartelle. Il pannello «I miei preferiti» ha la cartella ${q(RESERVED_NAME)}, ` +
        "con le fonti scelte con il pin in Ultime news, e una cartella per ogni feed che crei. Una cartella è sempre " +
        "aperta: le sue news sono a destra.",
      "«Nuovo feed» chiede un nome e le fonti; le fonti di un feed si scelgono solo tra i preferiti.",
      "«Modifica» cambia nome e fonti del feed; «Elimina» lo toglie dopo una conferma.",
      "Senza preferiti la pagina lo dice: «Nessuna fonte preferita: usa il pin in Ultime news.»",
    ],
    links: [FEED],
  },
  {
    id: "parlamento",
    title: "Parlamento",
    points: [
      "Il Parlamento è un ente di Ultime news: nell’albero trovi Camera e Senato con le loro sezioni (Plenaria, " +
        "Commissioni, atti, dossier).",
      "Con «Plenaria» o «Commissioni» nel filtro, al posto delle card dei loro documenti vedi le sedute: una lista per " +
        "ramo, «Camera» e «Senato», con «Dati aggiornati alle …» e una riga per seduta con lo stato («Convocata alle …», " +
        "«In corso dalle …», «Terminata alle …»). La seduta in corso è in evidenza e la lista si aggiorna da sola.",
      "L’indirizzo «/parlamento» apre direttamente la vista delle quattro fonti delle sedute: «Ultime news · Parlamento · " +
        "Sedute».",
      "La pagina di una seduta ha il titolo («Senato · Plenaria · Seduta n. 467»), la riga con data, stato e ora dei " +
        "dati, e la lista «Documenti»: resoconti, allegati, ordine del giorno; per le Commissioni i resoconti o i " +
        "comunicati del bollettino.",
      "Un documento con i punti si apre sul posto e «Apri il documento completo» porta alla sua pagina; uno senza indice " +
        "si apre in pagina. «Torna alle sedute» riporta alla vista da cui l’hai aperta.",
      `La pagina di un documento con l’indice ha a sinistra ${q(INDEX_TITLE)}, con «Mostra voci di routine», «Mostra ` +
        `interventi» e «Mostra note»; ogni voce porta al punto del testo. ${q(OPEN_OFFICIAL)} mostra la pagina del sito ` +
        "della Camera o del Senato dentro Politor. I documenti si salvano con il segnalibro come le news; le righe delle " +
        "sedute no.",
    ],
    links: [SITTINGS],
  },
  {
    id: "legenda",
    title: "Icone e stati",
    points: [
      "«News dall’ultimo accesso» e «News precedenti»: la visita si registra a ogni apertura dell’area News e divide le " +
        "due liste; la data a destra del titolo è quella della visita precedente.",
      "«Carica altre», in fondo a una lista, prende la pagina successiva.",
      "Quando una chiamata fallisce, una riga lo dice e «Riprova» la ripete; le card già lette restano.",
      "«Fonti eliminate»: le fonti tolte dal catalogo, barrate, sotto l’albero delle fonti.",
    ],
    legend: [
      {
        key: "ingestion",
        icon: "ingestion",
        text: "File verde: gli articoli della fonte hanno allegati testuali importati, leggibili in Politor; passa il mouse per il messaggio.",
      },
      {
        key: "warning",
        icon: "triangleExclamation",
        text: "«Fonte con avvisi»: almeno una sezione dell’ente non si aggiorna; la sezione è barrata in rosso e il tooltip dice il dettaglio.",
      },
      {
        key: "pin-off",
        icon: "pinOutline",
        text: "Pin spento: l’ente non è tra i preferiti; cliccalo per salvarlo e scegliere le cartelle.",
      },
      {
        key: "pin-on",
        icon: "pin",
        text: "Pin acceso: l’ente è tra i preferiti, anche solo con alcune sezioni («(in parte)»); cliccalo per gestire le cartelle.",
      },
      {
        key: "bookmark-off",
        icon: "bookmarkOutline",
        text: "Segnalibro vuoto: «Salva news» porta la news in News salvate.",
      },
      { key: "bookmark-on", icon: "bookmark", text: "Segnalibro pieno: la news è salvata; cliccalo per toglierla." },
      { key: "chevron-down", icon: "chevronDownWide", text: "Chevron verso il basso: la card si apre sul posto." },
      {
        key: "chevron-right",
        icon: "chevronDownWide",
        className: "-rotate-90",
        text: "Chevron verso destra: la news, la seduta o il documento si apre nella sua pagina.",
      },
      {
        key: "box",
        icon: "squareCheck",
        text: "Casella spuntata o vuota: la fonte è dentro o fuori dal filtro. Un ente con solo alcune sezioni spuntate mostra la casella vuota.",
      },
      { key: "box-dashed", icon: "squareDashed", text: "Casella tratteggiata: la sezione non si aggiorna." },
      {
        key: "box-muted",
        icon: "squareCheckMuted",
        text: "Casella grigia: in Il mio feed le fonti di una cartella sono solo elencate; si cambiano con «Modifica».",
      },
      {
        key: "switch",
        icon: "circleCheck",
        text: "Interruttore acceso o spento: le selezioni rapide del pannello delle fonti e gli interruttori dell’indice.",
      },
      {
        key: "folder",
        icon: "folderOpen",
        text: "Cartella aperta o chiusa: le cartelle di Il mio feed e gli anni del filtro per data.",
      },
      {
        key: "panel",
        icon: "sliders",
        text:
          "Il pannello di sinistra, chiuso quando apri Politor: la striscia con l’icona lo apre, il chevron accanto " +
          "al titolo lo chiude; resta come lo lasci in ogni pagina e resta in vista mentre la pagina scorre.",
      },
      {
        key: "external",
        icon: "arrowUpRightFromSquare",
        text: "«Apri link esterno»: il sito della fonte in una nuova scheda.",
      },
      { key: "ai", icon: "magicWand", text: "«Rielabora con AI»: in sviluppo." },
    ],
    links: [LATEST],
  },
  {
    id: "accesso",
    title: "Accesso e uscita",
    points: [
      "Entri in Politor con l’account aziendale (Microsoft Entra): senza sessione la pagina ti porta al login e poi " +
        "torna dove eri.",
      "Il menu con le tue iniziali, in alto a destra, mostra nome e indirizzo, apre questa guida, dice in fondo la " +
        "versione dell’app e ha «Esci» solo quando l’accesso avviene con Entra.",
      "Se un accesso non va a buon fine, la pagina «Accesso a Politor» dice perché (account non abilitato, troppi gruppi, " +
        "accesso scaduto, errore) e «Riprova ad accedere» ricomincia.",
      "Se all’apertura Politor non riesce a verificare l’accesso, lo dice e «Riprova» ci riprova.",
    ],
    links: [],
  },
  {
    id: "in-sviluppo",
    title: IN_DEVELOPMENT,
    points: [
      `Alcune voci sono già al loro posto ma non fanno ancora nulla: le riconosci dalla scritta ${q(IN_DEVELOPMENT)}, ` +
        "accanto al titolo della pagina o dentro il pulsante.",
      "«Ricerca»: «La ricerca per parole nelle news e nei documenti del Parlamento arriverà in un prossimo rilascio.»",
      "«Gestione alert»: «Gli avvisi su parole chiave e fonti arriveranno in un prossimo rilascio.»",
      "«Rielabora con AI», nella card aperta: «Per ora non produce alcun testo.»",
    ],
    links: [
      { to: "/ricerca", label: "Vai a Ricerca" },
      { to: "/alert", label: "Vai a Gestione alert" },
    ],
  },
];

/** Where the link to the sittings lands: the view of the four sources of sittings (router.tsx redirects /parlamento there). */
export const SITTINGS_LANDING = SITTINGS_VIEW_PATH;
